import React, { useState } from 'react';
import styled from 'styled-components';
import theme from '../../theme';

var CATEGORY_COLORS = {
  breakfast: theme.breakfast,
  lunch: theme.lunch,
  dinner: theme.dinner,
  snack: theme.snack,
  dessert: theme.dessert,
  side: theme.side
};

var Card = styled.div`
  background: ${theme.bgCard};
  border: 1px solid ${theme.border};
  border-radius: ${theme.radius};
  padding: 20px;
  transition: border-color 0.15s;
  &:hover { border-color: ${theme.borderLight}; }
`;

var CardHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 12px;
`;

var RecipeTitle = styled.h3`
  margin: 0;
  color: #fff;
  font-size: 1.1rem;
`;

var FavBtn = styled.button`
  background: none;
  border: none;
  font-size: 1.2rem;
  cursor: pointer;
  padding: 4px;
  opacity: ${p => p.$fav ? 1 : 0.3};
  &:hover { opacity: 1; }
`;

var CategoryBadge = styled.span`
  display: inline-block;
  padding: 4px 10px;
  border-radius: ${theme.radiusPill};
  font-size: 0.7rem;
  font-weight: 700;
  text-transform: uppercase;
  background: ${p => p.$color ? p.$color + '22' : 'rgba(255,255,255,0.1)'};
  color: ${p => p.$color || theme.textMuted};
  border: 1px solid ${p => p.$color ? p.$color + '44' : theme.border};
  margin-bottom: 8px;
`;

var Meta = styled.div`
  display: flex;
  gap: 16px;
  margin: 8px 0;
  font-size: 0.8rem;
  color: ${theme.textMuted};
`;

var Desc = styled.p`
  color: ${theme.textMuted};
  font-size: 0.85rem;
  margin: 8px 0;
  line-height: 1.4;
`;

var Tags = styled.div`
  display: flex;
  gap: 6px;
  flex-wrap: wrap;
  margin: 8px 0;
`;

var Tag = styled.span`
  padding: 2px 8px;
  border-radius: ${theme.radiusPill};
  font-size: 0.7rem;
  background: rgba(255,255,255,0.08);
  color: ${theme.textMuted};
`;

var IngredientsList = styled.div`
  margin: 12px 0;
  padding: 12px;
  background: rgba(0,0,0,0.2);
  border-radius: ${theme.radiusSm};
  max-height: ${p => p.$expanded ? 'none' : '120px'};
  overflow: hidden;
  position: relative;
`;

var IngTitle = styled.div`
  font-size: 0.75rem;
  font-weight: 700;
  color: ${theme.accent};
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-bottom: 6px;
`;

var Ingredient = styled.div`
  font-size: 0.8rem;
  color: ${theme.text};
  padding: 2px 0;
`;

var ExpandBtn = styled.button`
  background: none;
  border: none;
  color: ${theme.accent};
  font-size: 0.75rem;
  cursor: pointer;
  padding: 4px;
  &:hover { text-decoration: underline; }
`;

var Actions = styled.div`
  display: flex;
  gap: 8px;
  margin-top: 12px;
`;

var ActionBtn = styled.button`
  flex: 1;
  padding: 10px;
  border: 1px solid ${theme.border};
  border-radius: ${theme.radiusSm};
  background: transparent;
  color: ${p => p.$color || theme.textMuted};
  cursor: pointer;
  font-size: 0.8rem;
  min-height: 40px;
  &:hover { border-color: ${p => p.$color || theme.accent}; }
`;

var RecipeCard = function ({ recipe, onEdit, onDelete, onToggleFavorite }) {
  var [expanded, setExpanded] = useState(false);
  var color = CATEGORY_COLORS[recipe.category] || theme.accent;
  var totalTime = (recipe.prepTime || 0) + (recipe.cookTime || 0);

  return (
    <Card>
      <CardHeader>
        <div>
          <RecipeTitle>{recipe.title}</RecipeTitle>
          <CategoryBadge $color={color}>{recipe.category}</CategoryBadge>
        </div>
        <FavBtn $fav={recipe.favorite} onClick={onToggleFavorite}>
          {recipe.favorite ? '⭐' : '☆'}
        </FavBtn>
      </CardHeader>

      <Meta>
        {recipe.servings > 0 && <span>🍽️ {recipe.servings} servings</span>}
        {recipe.prepTime > 0 && <span>⏱️ {recipe.prepTime}m prep</span>}
        {recipe.cookTime > 0 && <span>🔥 {recipe.cookTime}m cook</span>}
        {totalTime > 0 && <span>⏰ {totalTime}m total</span>}
      </Meta>

      {recipe.description && <Desc>{recipe.description}</Desc>}

      {recipe.tags?.length > 0 && (
        <Tags>{recipe.tags.map(function (t) { return <Tag key={t}>{t}</Tag>; })}</Tags>
      )}

      {recipe.ingredients?.length > 0 && (
        <IngredientsList $expanded={expanded}>
          <IngTitle>Ingredients ({recipe.ingredients.length})</IngTitle>
          {recipe.ingredients.map(function (ing, i) {
            return <Ingredient key={i}>{ing.amount} {ing.unit} {ing.name}</Ingredient>;
          })}
        </IngredientsList>
      )}

      {recipe.ingredients?.length > 4 && !expanded && (
        <ExpandBtn onClick={function () { setExpanded(true); }}>Show all ingredients</ExpandBtn>
      )}

      <Actions>
        <ActionBtn $color={theme.info} onClick={onEdit}>Edit</ActionBtn>
        <ActionBtn $color={theme.error} onClick={onDelete}>Delete</ActionBtn>
      </Actions>
    </Card>
  );
};

export default RecipeCard;
